import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import KeyboardDoubleArrowUpIcon from "@mui/icons-material/KeyboardDoubleArrowUp";
import {
	Chip,
	FormControlLabel,
	Paper,
	Stack,
	Switch,
	Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { ExcelExportParams } from "ag-grid-community";
import { Fragment, type ReactNode, useState } from "react";

import { TestShackReportExcelExport } from "./TestShackReportExcelExport";

interface SectionRevKey {
	key: string;
}

interface SectionRevRenderParams<TSectionRev> {
	sectionRev: TSectionRev;
	isCollapsed: boolean;
	onToggle: () => void;
}

interface TestShackReportSectionRevListProps<TSectionRev> {
	sectionRevs: TSectionRev[];
	inspectionCount: number;
	isLoading: boolean;
	problemsOnly: boolean;
	showMinTargetMax: boolean;
	onProblemsOnlyChange: (problemsOnly: boolean) => void;
	onShowMinTargetMaxChange: (showMinTargetMax: boolean) => void;
	getExportParams: () => ExcelExportParams;
	renderSectionRev: (params: SectionRevRenderParams<TSectionRev>) => ReactNode;
	onError: (error: Error) => void;
}

export const TestShackReportSectionRevList = <
	TSectionRev extends SectionRevKey,
>({
	sectionRevs,
	inspectionCount,
	isLoading,
	problemsOnly,
	showMinTargetMax,
	onProblemsOnlyChange,
	onShowMinTargetMaxChange,
	getExportParams,
	renderSectionRev,
	onError,
}: TestShackReportSectionRevListProps<TSectionRev>) => {
	const [collapsedKeys, setCollapsedKeys] = useState<ReadonlySet<string>>(
		new Set<string>(),
	);
	const isAllCollapsed =
		sectionRevs.length > 0 &&
		sectionRevs.every((sectionRev) => collapsedKeys.has(sectionRev.key));

	const toggleSectionRev = (key: string) =>
		setCollapsedKeys((current) => {
			const next = new Set(current);
			if (!next.delete(key)) next.add(key);
			return next;
		});

	const toggleAll = () =>
		setCollapsedKeys(
			isAllCollapsed
				? new Set<string>()
				: new Set(sectionRevs.map((sectionRev) => sectionRev.key)),
		);

	return (
		<Stack spacing={1.5}>
			<Paper
				elevation={1}
				sx={{
					alignItems: "center",
					display: "flex",
					flexWrap: "wrap",
					gap: 1,
					justifyContent: "space-between",
					px: 1.5,
					py: 1,
				}}
			>
				<Typography variant="body2" color="text.secondary" fontWeight={700}>
					{sectionRevs.length === 1
						? "1 Section-Rev"
						: `${sectionRevs.length} Section-Revs`}
					{" · "}
					{inspectionCount === 1
						? "1 inspection"
						: `${inspectionCount} inspections`}
				</Typography>
				<Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
					<FormControlLabel
						control={
							<Switch
								size="small"
								checked={problemsOnly}
								onChange={(event) => onProblemsOnlyChange(event.target.checked)}
							/>
						}
						label="Out of range only"
						slotProps={{
							typography: { fontSize: "0.8125rem", fontWeight: 500 },
						}}
						sx={{ whiteSpace: "nowrap" }}
					/>
					<FormControlLabel
						control={
							<Switch
								size="small"
								checked={showMinTargetMax}
								onChange={(event) =>
									onShowMinTargetMaxChange(event.target.checked)
								}
							/>
						}
						label="Show Min/Target/Max"
						slotProps={{
							typography: { fontSize: "0.8125rem", fontWeight: 500 },
						}}
						sx={{ mr: 2, whiteSpace: "nowrap" }}
					/>
					<Chip
						clickable
						aria-pressed={isAllCollapsed}
						disabled={sectionRevs.length === 0}
						icon={
							isAllCollapsed ? (
								<KeyboardDoubleArrowDownIcon sx={{ fontSize: 16 }} />
							) : (
								<KeyboardDoubleArrowUpIcon sx={{ fontSize: 16 }} />
							)
						}
						label={isAllCollapsed ? "Expand all" : "Collapse all"}
						onClick={toggleAll}
						sx={{
							backgroundColor: (theme) =>
								alpha(theme.palette.primary.main, 0.08),
							border: 1,
							borderColor: (theme) => alpha(theme.palette.primary.main, 0.4),
							borderRadius: 1,
							color: "primary.main",
							fontSize: "0.8125rem",
							fontWeight: 600,
							height: 30,
							px: 0.5,
							"& .MuiChip-icon": { color: "inherit", ml: 0.75, mr: -0.25 },
							"&:hover": {
								backgroundColor: (theme) =>
									alpha(theme.palette.primary.main, 0.16),
								borderColor: "primary.main",
							},
						}}
					/>
					<TestShackReportExcelExport
						hasRows={sectionRevs.length > 0}
						getExportParams={getExportParams}
						disabled={isLoading}
						onError={onError}
					/>
				</Stack>
			</Paper>

			{sectionRevs.map((sectionRev) => (
				<Fragment key={sectionRev.key}>
					{renderSectionRev({
						sectionRev,
						isCollapsed: collapsedKeys.has(sectionRev.key),
						onToggle: () => toggleSectionRev(sectionRev.key),
					})}
				</Fragment>
			))}
		</Stack>
	);
};
